import type { ItemFieldRow } from './item-field-reader';
import { calculateVitalityStatus } from './vitality-reader';
import { phpAdd, phpSubtract, phpMultiply, phpDivide, phpLess, phpMax, phpFloatToInteger, phpJsonInteger } from './php-integer-arithmetic';
import type { PhpArithmetic } from './php-integer-arithmetic';
import { phpJsonTruthy } from './php-json-values';

const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const own = (value: unknown, key: string): unknown => arrayLike(value) && Object.prototype.hasOwnProperty.call(value, key)
    ? (value as Readonly<Record<string, unknown>>)[key] : undefined;
function clone(value: unknown): unknown {
    if (!arrayLike(value)) return value ?? null;
    const entries = Object.entries(value);
    return entries.every(([key], index) => key === String(index)) ? entries.map(([, child]) => clone(child))
        : Object.fromEntries(entries.map(([key, child]) => [key, clone(child)]));
}
function writable(value: unknown): Record<string, unknown> {
    if (value == null || value === false) return {};
    if (!arrayLike(value)) throw new TypeError('Cannot write PHP array offsets on a scalar');
    return Object.fromEntries(Object.entries(value));
}
export interface VitalityBuildDependencies {
    readVitality: (row: ItemFieldRow) => ItemFieldRow | null;
    readLayerValue: (row: ItemFieldRow, key: string) => unknown;
    readItemField: (row: ItemFieldRow, key: string) => unknown;
    readStatuses: (row: ItemFieldRow) => ItemFieldRow;
    readBlightedStack: (row: ItemFieldRow, statuses: ItemFieldRow) => number | null;
    buildStatusShell: (id: string) => ItemFieldRow | null;
}
const leanKeys = ['base_v_temp', 'base_v_max_temp', 'v_temp', 'v_max_temp', 'v_temp_percent',
    'v_max_temp_percent', 'v_bar_v_temp_percent', 'v_lost', 'v_max_reduction'];
export function leanVitality(input: ItemFieldRow): ItemFieldRow {
    const result = writable(input);
    for (const key of leanKeys) if (!phpJsonTruthy(own(result, key))) delete result[key];
    return clone(result) as ItemFieldRow;
}
/** One formula owner for immutable initial compilation and held-cluster deltas. */
function deriveVitality(v: Record<string, unknown>, target: ItemFieldRow, read: (key: string) => bigint,
    deps: VitalityBuildDependencies): boolean {
    const current = read('v_current'), maximum = read('v_max'), temp = read('v_temp'), maxTemp = read('v_max_temp');
    Object.assign(v, { base_v_current: Number(current), base_v_max: Number(maximum), base_v_temp: Number(temp), base_v_max_temp: Number(maxTemp) });
    const type = deps.readItemField(target, 'item_type');
    const living = type === 'creature' || type === 'player_character';
    let reduction = 0n;
    const statuses = living ? deps.readStatuses(target) : [];
    if (living && phpJsonTruthy(statuses)) {
        const stacks = deps.readBlightedStack(target, statuses) ?? 0;
        if (stacks > 0) {
            const multiplier = 0.1 * stacks;
            reduction = phpFloatToInteger(Math.floor(Number(phpMultiply(phpAdd(maximum, maxTemp), multiplier))));
            v.blight_multiplier = multiplier; v.blighted_v = Number(reduction);
        }
    }
    if (reduction === 0n) { delete v.blight_multiplier; delete v.blighted_v; }
    const effectiveMax = phpMax(1n, phpSubtract(phpAdd(maximum, maxTemp), reduction));
    const effectiveCurrent = phpMax(0n, phpLess(effectiveMax, current) ? effectiveMax : current);
    const total = phpAdd(effectiveMax, temp);
    const percent = (value: PhpArithmetic): number => phpLess(0n, total)
        ? Number(phpFloatToInteger(Math.floor(Number(phpMultiply(phpDivide(value, total), 100n))))) : 0;
    Object.assign(v, { v_current: Number(effectiveCurrent), v_temp: Number(temp), v_max: Number(maximum), v_max_temp: Number(maxTemp),
        effective_v_max: Number(effectiveMax), v_max_reduction: Number(reduction), v_lost: Number(phpSubtract(effectiveMax, effectiveCurrent)),
        v_percent: percent(effectiveCurrent) });
    const statusId = calculateVitalityStatus(effectiveCurrent, effectiveMax, temp);
    v.v_status = clone(deps.buildStatusShell(statusId) ?? statusId);
    Object.assign(v, { v_temp_percent: percent(temp), v_max_temp_percent: percent(maxTemp),
        v_bar_v_percent: percent(effectiveCurrent), v_bar_v_temp_percent: percent(temp), v_bar_total: Number(total) });
    for (const key of leanKeys) if (!phpJsonTruthy(own(v, key))) delete v[key];
    if (!living) {
        for (const key of Object.keys(v)) if (!['base_v_current', 'base_v_max', 'base_v_temp', 'base_v_max_temp'].includes(key)) delete v[key];
    }
    return phpLess(effectiveCurrent, current);
}

/** Immutable initial/source-row API. Only initial compilation copies the target. */
export function compileVitality(target: ItemFieldRow, source: ItemFieldRow | null, deps: VitalityBuildDependencies): { row: ItemFieldRow; burn: boolean } {
    const bases = source === null ? deps.readVitality(target) ?? [] : source;
    const result = writable(clone(target));
    delete result.v_arr;
    const computed = writable(own(result, 'computed'));
    delete computed.v_arr;
    const v = writable(own(computed, 'vitality'));
    result.computed = computed; computed.vitality = v;
    const burn = deriveVitality(v, result, key => phpJsonInteger(source === null
        ? deps.readLayerValue(bases, `base_${key}`) ?? 0 : own(bases, key) ?? 0), deps);
    return { row: result, burn };
}

export type VitalityChanges = Partial<Record<'v_current' | 'v_max' | 'v_temp' | 'v_max_temp', number>>;
/** Patch the held vitality object without copying its actor or unrelated clusters.
 * The reader merges lean inventory definition defaults under instance overrides.
 * Explicit zero changes take precedence over an omitted lean base.
 */
export function patchVitality(target: Record<string, unknown>, changes: VitalityChanges, deps: VitalityBuildDependencies): boolean {
    const bases = deps.readVitality(target) ?? [];
    const computed = (target.computed ??= {}) as Record<string, unknown>;
    const v = (computed.vitality ??= {}) as Record<string, unknown>;
    return deriveVitality(v, target, key => phpJsonInteger(own(changes, key)
        ?? deps.readLayerValue(bases, `base_${key}`) ?? deps.readLayerValue(bases, key) ?? 0), deps);
}
